'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { pushNewsletterFormSubmit } from '@/utilities/gtmFormSubmit'

export const NewsletterForm: React.FC = () => {
  const [email, setEmail] = useState('')
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      setMessage({ type: 'error', text: 'Bitte gebe deine E-Mail-Adresse ein.' })
      return
    }

    if (!privacyAccepted) {
      setMessage({
        type: 'error',
        text: 'Bitte bestätige, dass du die Datenschutzerklärung gelesen hast.',
      })
      return
    }

    setIsSubmitting(true)
    setMessage(null)

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'Footer', privacyAccepted: true }),
      })

      const result = await response.json()

      if (result.success) {
        pushNewsletterFormSubmit({
          formId: 'newsletter-footer',
          formTarget: typeof window !== 'undefined' ? window.location.pathname : undefined,
        })
        setMessage({
          type: 'success',
          text:
            result.message ||
            'Fast geschafft! Bitte bestätige deine Anmeldung über den Link in der E-Mail, die wir dir gerade geschickt haben.',
        })
        setEmail('')
        setPrivacyAccepted(false)
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
        })
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex max-w-sm flex-col gap-4">
      <span className="font-clash text-sm font-light md:text-3xl">
        Nicht nur Erster am Berg, sondern auch Erster mit Updates.
      </span>
      <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2">
        <input
          type="email"
          placeholder="Deine E-Mail-Adresse"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="focus:outline-hidden focus:ring-accent-500 flex-1 rounded border border-gray-300 bg-white px-3 py-3 text-sm text-black focus:ring-2"
        />
        <label className="flex items-start gap-2 text-xs leading-snug text-white">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            required
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent-500"
          />
          <span>
            Ich habe die{' '}
            <Link href="/datenschutz" target="_blank" className="underline">
              Datenschutzerklärung
            </Link>{' '}
            gelesen und stimme der Verarbeitung meiner E-Mail-Adresse zum Versand des Newsletters
            zu. Die Einwilligung kann ich jederzeit widerrufen.
          </span>
        </label>
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-accent-500 flex items-center py-3 font-clash text-sm text-white transition-all hover:no-underline disabled:opacity-50 md:text-lg"
        >
          {isSubmitting ? 'WIRD ANGEMELDET...' : 'Zum Newsletter anmelden'}
          <ChevronRight />
        </button>
      </form>
      {message && (
        <div
          className={`rounded-lg mt-2 p-3 text-sm ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  )
}
